import { combineReducers } from 'redux';
import { configureStore, getDefaultMiddleware } from '@reduxjs/toolkit';
import { createWrapper } from 'next-redux-wrapper';
import { runWithRequestScope } from '../api/request-scope.mjs';

// reducers
import auth from 'store/auth';
import ux from 'store/ux';
import company from 'store/company';
import expertise from 'store/expertise';
import alerts from 'store/alert';
import navigationSearch from 'store/navigation-search';
import geo from './geo';
import currentUser from './currentUser';
import { publicAlert, publicResponses } from '../utils/public-alert.js';

// root reducer which combines all the reducers to the store
const rootReducer = combineReducers({
    auth,
    company,
    expertise,
    ux,
    alerts,
    navigationSearch,
    geo,
    currentUser,
});

// TODO: Find a better way than storing functions in redux store
const middlewareConfig = {
    serializableCheck: {
        // Ignore these action types and paths
        ignoredActions: ['ux/setPopupConfig'],
        ignoredActionPaths: [
            'payload.ux.popup.onConfirm',
            'payload.ux.popup.onCancel',
        ],
        ignoredPaths: ['ux.popup.onConfirm', 'ux.popup.onCancel'],
    },
};

// create store with redux-toolkit
// https://redux-toolkit.js.org/api/configureStore#configurestore
const makeStore = () =>
    configureStore({
        reducer: rootReducer,
        devTools: process.env.NODE_ENV !== 'production',
        middleware:
            process.env.NODE_ENV !== 'production' // eslint-disable-line
                ? getDefaultMiddleware(middlewareConfig).concat()
                : // ? getDefaultMiddleware(middlewareConfig)
                  getDefaultMiddleware(middlewareConfig),
    });

// A getStaticProps store never saw the user, and a logged-out
// getServerSideProps store has nothing to say about a client that may still be
// signed in. Only an authenticated server state may overwrite the client's auth
// and currentUser slices on HYDRATE.
//
// The alerts slice is published on the ISR alert pages, so it goes through the same
// whitelist as the page props (utils/public-alert.js).
// Only rewrite keys the slice has: initialState has no `alert`, and an `undefined`
// value fails getStaticProps serialization (broke /breaking-news prerender).
const publicAlertsSlice = (alerts) =>
    alerts
        ? {
              ...alerts,
              ...('alert' in alerts
                  ? { alert: publicAlert(alerts.alert) ?? null }
                  : {}),
              ...('alertResponses' in alerts
                  ? {
                        alertResponses:
                            publicResponses(alerts.alertResponses) ?? null,
                    }
                  : {}),
          }
        : alerts;

const serializeState = ({ auth, currentUser, alerts, ...rest }) => {
    const safe = { ...rest, alerts: publicAlertsSlice(alerts) };
    return auth.isAuthenticated ? { auth, currentUser, ...safe } : safe;
};

// https://github.com/kirill-konshin/next-redux-wrapper#app
const base = createWrapper(makeStore, { serializeState });

const scoped = (method) => (callback) =>
    base[method](
        (store) => (context) =>
            runWithRequestScope(() => callback(store)(context))
    );

export const wrapper = {
    ...base,
    getServerSideProps: scoped('getServerSideProps'),
    getStaticProps: scoped('getStaticProps'),
};
